/**
 * All API calls related to user setup & data fetching during navigation.
 * Keeping this separate means guards stay readable and this logic is testable in isolation.
 */

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import store from '@/store';

const BASE_URL = process.env.VUE_APP_CBE_BASE_URL;

const authHeaders = token => ({ Authorization: `Bearer ${token}` });

// ── API calls ────────────────────────────────────────────────────────────────

async function getOrCreatePiccoloUser(auth0User, token) {
  try {
    const { data } = await axios.get(
      `${BASE_URL}/piccolo/users/${auth0User.sub}`,
      { headers: authHeaders(token) }
    );
    return data;
  } catch (err) {
    if (err.response?.status !== 404) throw err;

    const { data } = await axios.post(
      `${BASE_URL}/piccolo/users`,
      {
        auth0_id: auth0User.sub,
        email: auth0User.email,
        organization: '',
        terms_version: Number(process.env.VUE_APP_TERMS_VERSION),
      },
      { headers: authHeaders(token) }
    );
    return data;
  }
}

async function fetchBillingInfo(userId, token) {
  // Return cached value if already known
  const cached = store.state.authentication.hasBillingInfo;
  if (cached !== null) return cached;

  try {
    const { status } = await axios.get(
      `${BASE_URL}/piccolo/billing/address/${userId}`,
      { headers: authHeaders(token) }
    );
    return status === 200;
  } catch {
    return false;
  }
}

async function fetchSpecialFileAccess(userId, token) {
  try {
    const { data } = await axios.get(
      `${BASE_URL}/piccolo/specialfiles/${userId}`,
      { headers: authHeaders(token) }
    );
    return data.length > 0;
  } catch {
    return false;
  }
}

async function fetchQuoteStatus(userId, token) {
  try {
    const { data } = await axios.get(
      `${BASE_URL}/piccolo/billing/quote-status/${userId}`,
      { headers: authHeaders(token) }
    );
    return data.has_quote;
  } catch (err) {
    console.error('Error fetching quote status:', err);
    return false;
  }
}

// ── Public entry point ───────────────────────────────────────────────────────

export async function loadUserData(auth0User, getAccessTokenSilently) {
  const token = await getAccessTokenSilently();
  const decoded = jwtDecode(token);

  const [piccoloUser, hasBillingInfo, hasSpecialFileAccess, hasQuote] = await Promise.all([
    getOrCreatePiccoloUser(auth0User, token),
    fetchBillingInfo(auth0User.sub, token),
    fetchSpecialFileAccess(auth0User.sub, token),
    fetchQuoteStatus(auth0User.sub, token),
  ]);

  const permissions = decoded.permissions;
  const roles = auth0User['https://structure.io/roles'];

  store.commit('SET_HAS_SPECIAL_FILE_ACCESS', hasSpecialFileAccess);
  store.commit('SET_AUTHENTICATION', {
    userIsAuthorized: true,
    roles,
    permissions,
    termsVersion: piccoloUser.terms_version,
    is_admin: piccoloUser.is_admin,
    account_status: piccoloUser.account_status,
    tier: piccoloUser.tier,
    hasBillingInfo,
    hasQuote,
    is_trial: piccoloUser.is_trial,
  });

  return { ...piccoloUser, has_billing_info: hasBillingInfo, has_quote: hasQuote };
}